import type { RequestDTO as EvaluateExpressionRequestDTO, ResponseDTO as EvaluateExpressionResponseDTO } from '@dwp/api-parser/dist/api/Schema/Schema.EvaluateExpression';
import type { ObjectClassInfo } from '@dwp/api-parser/dist/api/Schema/classes/Schema/Classes.GetClasses';
import type { ObjectClassSchema, ClassType, SchemaObjectInfo } from '@dwp/api-parser/dist/api/Schema/classes/Schema/Classes.GetClass';
import type { ObjectTypeInfo } from '@dwp/api-parser/dist/api/Schema/types/Schema/Types.GetTypes';
import type { ObjectTypeSchema } from '@dwp/api-parser/dist/api/Schema/types/Schema/Types.GetType';
import {
	RequestDTO as GetObjectReferenceExpressionRequestDTO,
	ResponseDTO as GetObjectReferenceExpressionResponseDTO,
} from '@dwp/api-parser/dist/api/Schema/Schema.GetObjectReferenceExpression';

import { HttpService } from '../../../App/services/http-service';
import { WithRequired } from '../../../Types/WithRequired';

type SchemaItemTypes = 'types' | 'classes';
type SchemaObjectResponseType<T> = T extends 'types' ? Promise<TypeInfo[]> : T extends 'classes' ? Promise<ClassInfo[]> : never;

type SchemaItemInfo = Required<Omit<SchemaObjectInfo, 'ProtectionLevel'>> & { ProtectionLevel: number };

export type TypeSchema = SchemaItemInfo & WithRequired<ObjectTypeSchema, 'DisplayName' | 'MainClass'>;
export type TypeInfo = SchemaItemInfo & WithRequired<ObjectTypeInfo, 'MainClassName' | 'RelatedClasses'>;
export type ClassInfo = SchemaItemInfo & ObjectClassInfo;
export type ClassSchema = SchemaItemInfo & WithRequired<ObjectClassSchema, 'ClassType' | 'UsedInTypes'>;

export type SchemaItem = {
	DisplayName: string;
	Name: string;
	IsPickup?: boolean;
	Id: string;
};

type AttributeExpression = Required<GetObjectReferenceExpressionResponseDTO>;

type CacheType = {
	classes: null | Promise<ClassInfo[]>;
	types: null | Promise<TypeInfo[]>;
	attrExpressions: Record<string, Promise<AttributeExpression>>;
	classInfos: Record<string, Promise<ClassSchema>>;
};

/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.SchemaService
 * @module mx.SolutionBuilderAgent
 *
 * @description
 * A service provides methods for retrieving schema meta-data
 *
 */
export class SchemaService {
	private readonly cache: CacheType = {
		classes: null,
		types: null,
		attrExpressions: {},
		classInfos: {},
	};
	private readonly schemaServiceUrl = 'api/schema/';

	static $inject = ['mx.SolutionBuilderAgent.Http'];
	constructor(private readonly http: HttpService) {}

	public clearClasses(): void {
		this.cache.classes = null;
	}

	public clearTypes(): void {
		this.cache.types = null;
	}

	public getClassInfo(className: string): Promise<ClassSchema> {
		if (!this.cache.classInfos[className]) {
			this.cache.classInfos[className] = this.http.get<ClassSchema>(`${this.schemaServiceUrl}classes/${className}`);
		}
		return this.cache.classInfos[className];
	}

	public getClassInfoById(id: string): Promise<ClassSchema> {
		if (!this.cache.classInfos[id]) {
			this.cache.classInfos[id] = this.http.get<ClassSchema>(`${this.schemaServiceUrl}classes/${id}`);
		}
		return this.cache.classInfos[id];
	}

	public getType(key: string, keyIsId = false): Promise<TypeInfo | undefined> {
		return this._findSchemaItem<TypeInfo>(this.getTypes.bind(this), key, keyIsId);
	}

	/**
	 * @ngdoc method
	 * @name mx.SolutionBuilderAgent.SchemaService#evaluateColumnExpression
	 *
	 * @description
	 * Evaluates the validity of the data expression
	 *
	 *
	 * @param {string} entityClass - Name of the Entity class.
	 * @param {string} expr - expression for evaluation.
	 *
	 * @returns {string} null if expression is invalid, or name of data type of the expression result
	 */
	public async evaluateColumnExpression(entityClass: string, expr: string): Promise<string | null> {
		if (expr) {
			const params: EvaluateExpressionRequestDTO = {
				entityClass: entityClass,
				expr: expr,
			};
			return this.http.get<EvaluateExpressionResponseDTO>(`${this.schemaServiceUrl}evaluateExpression`, params);
		}

		return null;
	}

	public async getReferenceAttributeExpr(entityClass: string, attributeName: string, id: string): Promise<AttributeExpression | null> {
		const key = `${entityClass}.${attributeName}`.toLowerCase();

		const params: GetObjectReferenceExpressionRequestDTO = {
			entityClass: entityClass,
			attributeName: attributeName,
		};
		this.cache.attrExpressions[key] = this.cache.attrExpressions[key] || this.http.get<AttributeExpression>(`${this.schemaServiceUrl}attrExpr`, params);

		const data = await this.cache.attrExpressions[key];

		if (data) {
			return {
				EntityClassName: data.EntityClassName,
				FilterExpression: (data.FilterExpression || '').replace('@id', id),
			};
		}

		return null;
	}

	public getClass(key: string, keyIsId = false): Promise<ClassInfo | undefined> {
		return this._findSchemaItem<ClassInfo>(this.getClasses.bind(this), key, keyIsId);
	}

	private async _findSchemaItem<Type extends SchemaItemInfo>(itemsGetter: () => Promise<Type[]>, key: string, keyIsId = false): Promise<Type | undefined> {
		const types = await itemsGetter();
		return types.find(({ Id, InternalName }) => (keyIsId ? Id === key : InternalName === key));
	}

	public getTypeItems(valueIsId = false, className: string) {
		return this._getSchemaItems(this.getTypes.bind(this), className, valueIsId);
	}

	public getClassItems(valueIsId = false, typeName: string, classType: ClassType) {
		return this._getSchemaItems(this.getClasses.bind(this), null, valueIsId, typeName, classType);
	}

	private itemsAreTypes(items: SchemaItemInfo[], className: string | null): items is TypeInfo[] {
		return Boolean(className);
	}

	private itemsAreClasses(items: SchemaItemInfo[], classType?: number): items is ClassInfo[] {
		return typeof classType === 'number';
	}

	private async _getSchemaItems(
		func: () => Promise<SchemaItemInfo[]>,
		className: string | null,
		valueIsId = false,
		typeName?: string,
		classType?: number
	): Promise<SchemaItem[]> {
		let items = await func();
		if (this.itemsAreTypes(items, className)) {
			items = items.filter((item) => {
				const classes = item.RelatedClasses || [];
				className = (className || '').toUpperCase();
				return classes.some((_class) => _class.toUpperCase() === className);
			});
		} else if (typeof classType === 'number' && this.itemsAreClasses(items, classType)) {
			if (classType === -2) {
				items = items.filter((item) => item.ClassType !== 4 /*pickups*/);
			} else if (classType > -1 /*all*/) {
				items = items.filter((item) => item.ClassType === classType);
			}
		}

		if (typeName) {
			typeName = (typeName || '').toUpperCase();
			const types = await this.getTypes();
			let classes: string[] = [];

			types.some((type) => {
				if (type.InternalName.toUpperCase() === typeName) {
					classes = type.RelatedClasses;
					return true;
				}
				return false;
			});
			items = items.filter((item) => classes.some((_item) => item.InternalName === _item));
		}

		return items.map((item) => ({
			DisplayName: item.DisplayName + ' (' + item.InternalName + ')',
			Name: valueIsId ? item.Id : item.InternalName,
			IsPickup: (item as ClassInfo).IsPickup,
			Id: item.Id,
		}));
	}

	public getTypes(): Promise<TypeInfo[]> {
		return this._getSchemaObjects('types');
	}

	public getClasses(): Promise<ClassInfo[]> {
		return this._getSchemaObjects('classes');
	}

	private _getSchemaObjects<SchemaType extends SchemaItemTypes>(schemaType: SchemaType): SchemaObjectResponseType<SchemaType> {
		if (!this.cache[schemaType]) {
			(this.cache[schemaType] as SchemaObjectResponseType<SchemaType>) = this.http.get(`${this.schemaServiceUrl}${schemaType}`) as SchemaObjectResponseType<SchemaType>;
		}
		return this.cache[schemaType] as NonNullable<SchemaObjectResponseType<SchemaType>>;
	}
}
